import { apiClient } from './api-client';

/**
 * Report Template (Report Designer) API client — B6.9e.
 *
 * Mirrors the backend contracts established in B6.9b (read + lifecycle), B6.9c
 * (the design editor) and B6.9d (the version chain). It is the ONLY place these
 * shapes are declared on the frontend: the designer, the library and the version
 * history all import from here, so a contract change is a one-file change.
 *
 * Two rules the whole module follows:
 *
 *  - **The server is the authority on capability.** `canBeEngineerToggleable`,
 *    `isWidthEditable` and `minWidthFraction` are read from the response and
 *    never re-derived from a section key. The renderer's geometry lives in the
 *    backend and is not restated here.
 *  - **`tenantId` is neither sent nor received.** No endpoint accepts one, and
 *    the detail/summary shapes deliberately do not carry one.
 *
 * The axios response interceptor already unwraps the `{ data, meta }` envelope,
 * so `r.data` is the payload itself — never `{ data: … }` again.
 */

// ── Enums ─────────────────────────────────────────────────────────────────────

export type ReportTemplateStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';

/**
 * The four sections D-079 locks as mandatory structure.
 *
 * Section KEYS are stable identifiers (B6.9e brief §10), and this list restates
 * a business rule the read API does not expose as a field — not renderer
 * geometry and not a capability the API already reports. Everything the API
 * *does* report (`canBeEngineerToggleable`, `isWidthEditable`,
 * `minWidthFraction`, `defaultTitle`) is consumed from the response.
 *
 * `isStructural()` below is cross-checked against the API's own metadata by
 * `structuralMismatch()`, so if the server ever disagreed the UI would surface a
 * safe error rather than silently trusting this constant.
 */
export const STRUCTURAL_SECTION_KEYS = [
  'HEADER',
  'META_GRID',
  'CLASSIFICATION',
  'SIGNATURES',
] as const;

const STRUCTURAL_SET: ReadonlySet<string> = new Set(STRUCTURAL_SECTION_KEYS);

export function isStructuralSection(sectionKey: string): boolean {
  return STRUCTURAL_SET.has(sectionKey);
}

// ── Read shapes ───────────────────────────────────────────────────────────────

export interface ReportTemplateColumn {
  id: string;
  position: number;
  /** Fraction of the section's content width. Full `DECIMAL(18,17)` precision. */
  widthFraction: number;
  contentKey: string | null;
  isVisible: boolean;
}

export interface ReportTemplateSection {
  id: string;
  sectionKey: string;
  /** Override heading, or null meaning "use the renderer's built-in title". */
  title: string | null;
  /** What a null `title` renders as — supplied by the server, never guessed. */
  defaultTitle: string | null;
  position: number;
  isVisible: boolean;
  hideWhenEmpty: boolean;
  isEngineerToggleable: boolean;
  /** Whether the API would ACCEPT `isEngineerToggleable: true` for this key. */
  canBeEngineerToggleable: boolean;
  /** Whether the renderer measures this section's cells from the layout. */
  isWidthEditable: boolean;
  /**
   * EXCLUSIVE lower bound per column, as a fraction of the section. Derived
   * server-side from the renderer's own padding constants; 0 where there is
   * nothing to bound. Never recomputed in the browser.
   */
  minWidthFraction: number;
  options: Record<string, unknown> | null;
  columns: ReportTemplateColumn[];
}

export interface ReportTemplateSummary {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  rendererKey: string;
  status: ReportTemplateStatus;
  versionNumber: number;
  previousVersionId: string | null;
  isSystem: boolean;
  isDefault: boolean;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  sectionCount: number;
  visibleSectionCount: number;
  columnCount: number;
}

export interface ReportTemplateDetail extends ReportTemplateSummary {
  sections: ReportTemplateSection[];
}

export interface ReportTemplateListResult {
  data: ReportTemplateSummary[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/** One row of the B6.9d version history. Metadata only — no sections. */
export interface ReportTemplateVersion {
  id: string;
  name: string;
  versionNumber: number;
  previousVersionId: string | null;
  status: ReportTemplateStatus;
  isSystem: boolean;
  isDefault: boolean;
  rendererKey: string;
  createdByUserId: string | null;
  createdAt: string;
  publishedAt: string | null;
  publishedByUserId: string | null;
  updatedAt: string;
}

/**
 * The family document. `slug` identifies the family (D-078) — there is no
 * `familyId`, and the frontend must not invent one.
 */
export interface ReportTemplateVersionHistory {
  slug: string;
  requestedTemplateId: string;
  totalVersions: number;
  latestVersionNumber: number | null;
  /** The family's single open DRAFT, if it has one. Drives "revise" vs "resume". */
  openDraftTemplateId: string | null;
  defaultTemplateId: string | null;
  publishedVersionCount: number;
  versions: ReportTemplateVersion[];
}

// ── Write shapes ──────────────────────────────────────────────────────────────

export interface ReportTemplateListFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: ReportTemplateStatus;
  sortBy?: 'name' | 'status' | 'versionNumber' | 'publishedAt' | 'createdAt' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
}

export interface CreateReportTemplateDto {
  name: string;
  description?: string;
}

export interface UpdateReportTemplateDto {
  name?: string;
  description?: string;
}

export interface CloneReportTemplateDto {
  name?: string;
  description?: string;
}

/**
 * One section's complete desired design state (B6.9c).
 *
 * Declarative, not sparse: the payload must carry EVERY section of the template
 * exactly once, and `position`, `isVisible`, `hideWhenEmpty` and
 * `isEngineerToggleable` are all required. `title` absent ≡ `null` (no
 * override); `columnWidths` absent ≡ "keep the stored widths".
 */
export interface ReportTemplateSectionDesignInput {
  sectionKey: string;
  position: number;
  isVisible: boolean;
  hideWhenEmpty: boolean;
  isEngineerToggleable: boolean;
  title?: string | null;
  columnWidths?: number[];
}

export interface UpdateReportTemplateDesignDto {
  sections: ReportTemplateSectionDesignInput[];
}

export interface PublishResult {
  id: string;
  status: ReportTemplateStatus;
  versionNumber: number;
  isDefault: boolean;
  publishedAt: string;
}

export interface ArchiveResult {
  id: string;
  status: ReportTemplateStatus;
  versionNumber: number;
  archivedAt: string;
}

export interface SetDefaultResult {
  id: string;
  isDefault: boolean;
  previousDefaultTemplateId: string | null;
}

// ── Service ───────────────────────────────────────────────────────────────────

export const reportTemplateService = {
  async list(filters: ReportTemplateListFilters = {}): Promise<ReportTemplateListResult> {
    const r = await apiClient.get('/report-templates', { params: filters });
    return r.data as ReportTemplateListResult;
  },

  async get(templateId: string): Promise<ReportTemplateDetail> {
    const r = await apiClient.get(`/report-templates/${templateId}`);
    return r.data as ReportTemplateDetail;
  },

  async create(dto: CreateReportTemplateDto): Promise<ReportTemplateDetail> {
    const r = await apiClient.post('/report-templates', dto);
    return r.data as ReportTemplateDetail;
  },

  async update(templateId: string, dto: UpdateReportTemplateDto): Promise<ReportTemplateDetail> {
    const r = await apiClient.patch(`/report-templates/${templateId}`, dto);
    return r.data as ReportTemplateDetail;
  },

  /** B6.9c — replaces a DRAFT's whole section design atomically. */
  async updateDesign(
    templateId: string,
    dto: UpdateReportTemplateDesignDto,
  ): Promise<ReportTemplateDetail> {
    const r = await apiClient.patch(`/report-templates/${templateId}/design`, dto);
    return r.data as ReportTemplateDetail;
  },

  /** Starts a NEW family at v1. Not a new version — that is `createRevision`. */
  async clone(templateId: string, dto: CloneReportTemplateDto = {}): Promise<ReportTemplateDetail> {
    const r = await apiClient.post(`/report-templates/${templateId}/clone`, dto);
    return r.data as ReportTemplateDetail;
  },

  /** B6.9d — PUBLISHED → a new DRAFT version of the SAME family. */
  async createRevision(templateId: string): Promise<ReportTemplateDetail> {
    const r = await apiClient.post(`/report-templates/${templateId}/revisions`);
    return r.data as ReportTemplateDetail;
  },

  /** B6.9d — the family's version chain, from any member of it. */
  async getVersions(templateId: string): Promise<ReportTemplateVersionHistory> {
    const r = await apiClient.get(`/report-templates/${templateId}/versions`);
    return r.data as ReportTemplateVersionHistory;
  },

  async publish(templateId: string): Promise<PublishResult> {
    const r = await apiClient.post(`/report-templates/${templateId}/publish`);
    return r.data as PublishResult;
  },

  async archive(templateId: string): Promise<ArchiveResult> {
    const r = await apiClient.post(`/report-templates/${templateId}/archive`);
    return r.data as ArchiveResult;
  },

  async setDefault(templateId: string): Promise<SetDefaultResult> {
    const r = await apiClient.post(`/report-templates/${templateId}/set-default`);
    return r.data as SetDefaultResult;
  },

  async remove(templateId: string): Promise<void> {
    await apiClient.delete(`/report-templates/${templateId}`);
  },
};
